import IconBin from "@shared/components/icons/Bin"
import IconCog1 from "@shared/components/icons/Cog1"
import IconCogLine from "@shared/components/icons/CogLine"
import IconCommonFileDouble from "@shared/components/icons/CommonFileDouble"
import IconCopy from "@shared/components/icons/Copy"
import IconCopyPaste from "@shared/components/icons/CopyPaste"
import IconMinusCircle from "@shared/components/icons/MinusCircle"
import IconSubtractCircle from "@shared/components/icons/SubtractCircle"
import {
  DrilldownMenu,
  DrilldownRow,
  HeaderWithBackArrow,
  MenuHeader,
  MenuRoot,
  MenuTitle,
  Row,
  RowContent,
  RowIcon,
  RowLabel,
  Rows,
  Section,
  SectionChevron,
  SectionHeading,
  SectionIcon,
  Sections,
  SectionTitle,
} from "@shared/components/menus/DrilldownMenu"
import { ConfirmView } from "@shared/components/menus/views/ConfirmView"
import type { Course } from "@shared/models/course"
import { tracked } from "@supergrain/kernel/react"
import CourseHeaderMenuTrigger from "./CourseHeaderMenuTrigger"
import type { RemoveClassTodayChoice } from "./lesson-menu-views/remove-class-today-flow"
import { removeClassTodayFlow } from "./lesson-menu-views/remove-class-today-flow"
import { expandedSections } from "./non-instructional-menu-sections"

interface Props {
  course: Course
  /** The day this class sits on, which a note left here is pinned to. */
  dateString: string
  /** Whether the user can edit this plan; without it the menu offers Copy Note alone. */
  canEdit: boolean
  /** Opens the edit-course modal for the given courseId. */
  editCourseTitleAndOrder: (courseId: string) => void
  /** Copies the class's note to the user's clipboard (or prompts sign-in) and toasts. */
  copyLesson: () => void
  pasteLesson: () => void
  canPasteLesson: boolean
  /** Whether the day holds a note — the Erase Note row is hidden without one. */
  hasNote: boolean
  /** Clears the note, leaving the empty slot (and its Add Note button) behind. */
  eraseNote: () => void
  /** Turns this class off for the day, with an optional note left on it. */
  removeClassToday: (choice: RemoveClassTodayChoice) => void
}

/**
 * A non-instructional class's menu — the {@link import("./LessonMenu").default} shell with only
 * the rows that mean anything without a lesson to plan: no moving, printing, templates or
 * history, and Erase Note in place of Erase Lesson.
 */
export default tracked(function NonInstructionalMenu({
  course,
  dateString,
  canEdit,
  editCourseTitleAndOrder,
  copyLesson,
  pasteLesson,
  canPasteLesson,
  hasNote,
  eraseNote,
  removeClassToday,
}: Props) {
  const expandedIds = expandedSections.expandedIds

  const classHeader = (title: string) =>
    function Header({ back }: { back: () => void }) {
      return (
        <HeaderWithBackArrow backTo="Class Menu" onBack={back}>
          {title}
        </HeaderWithBackArrow>
      )
    }

  return (
    <DrilldownMenu
      ariaLabel="Class menu"
      trigger={<CourseHeaderMenuTrigger label="Class Menu" dataTest="class menu trigger" />}
      header={
        <MenuHeader>
          <MenuTitle>Class Menu</MenuTitle>
        </MenuHeader>
      }
    >
      {({ close }) => {
        const closeThen = (action: () => void) => () => {
          close()
          action()
        }
        return (
          <MenuRoot>
            <Sections>
              {canEdit ? (
                <>
                  <Section
                    typeaheadText="Copy and Paste"
                    isExpanded={expandedIds.has("copy-paste")}
                    onExpandedChange={(isExpanded) =>
                      expandedSections.setExpanded("copy-paste", isExpanded)
                    }
                    heading={
                      <SectionHeading>
                        <SectionIcon icon={IconCommonFileDouble} />
                        <SectionTitle>Copy and Paste</SectionTitle>
                        <SectionChevron />
                      </SectionHeading>
                    }
                  >
                    <Rows>
                      <Row typeaheadText="Copy Note" onAction={closeThen(copyLesson)}>
                        <RowContent>
                          <RowIcon icon={IconCopy} />
                          <RowLabel>Copy Note</RowLabel>
                        </RowContent>
                      </Row>
                      {canPasteLesson ? (
                        <Row typeaheadText="Paste Note" onAction={closeThen(pasteLesson)}>
                          <RowContent>
                            <RowIcon icon={IconCopyPaste} />
                            <RowLabel>Paste Note</RowLabel>
                          </RowContent>
                        </Row>
                      ) : null}
                    </Rows>
                  </Section>

                  <Section
                    typeaheadText="Remove"
                    isExpanded={expandedIds.has("remove")}
                    onExpandedChange={(isExpanded) =>
                      expandedSections.setExpanded("remove", isExpanded)
                    }
                    heading={
                      <SectionHeading>
                        <SectionIcon icon={IconSubtractCircle} />
                        <SectionTitle>Remove</SectionTitle>
                        <SectionChevron />
                      </SectionHeading>
                    }
                  >
                    <Rows>
                      <DrilldownRow
                        typeaheadText="Remove this class today"
                        {...removeClassTodayFlow({
                          hasLesson: false,
                          menuTitle: "Class Menu",
                          courseId: course.id,
                          color: course.attributes.color,
                          dateString,
                          onDone: removeClassToday,
                        })}
                      >
                        <RowContent>
                          <RowIcon icon={IconMinusCircle} />
                          <RowLabel>Remove this class today</RowLabel>
                        </RowContent>
                      </DrilldownRow>
                      {hasNote ? (
                        <DrilldownRow
                          typeaheadText="Erase Note"
                          header={classHeader("Delete the lesson?")}
                          body={({ back }) => (
                            <ConfirmView
                              body="Deleting this lesson will remove all notes."
                              confirmLabel="Delete"
                              destructive
                              onCancel={back}
                              onConfirm={closeThen(eraseNote)}
                            />
                          )}
                        >
                          <RowContent>
                            <RowIcon icon={IconBin} />
                            <RowLabel>Erase Note</RowLabel>
                          </RowContent>
                        </DrilldownRow>
                      ) : null}
                    </Rows>
                  </Section>

                  <Section
                    typeaheadText="Settings"
                    isExpanded={expandedIds.has("settings")}
                    onExpandedChange={(isExpanded) =>
                      expandedSections.setExpanded("settings", isExpanded)
                    }
                    heading={
                      <SectionHeading>
                        <SectionIcon icon={IconCog1} />
                        <SectionTitle>Settings</SectionTitle>
                        <SectionChevron />
                      </SectionHeading>
                    }
                  >
                    <Rows>
                      <Row
                        typeaheadText="Edit Class Settings"
                        onAction={closeThen(() => editCourseTitleAndOrder(course.id))}
                      >
                        <RowContent>
                          <RowIcon icon={IconCogLine} />
                          <RowLabel>Edit Class Settings</RowLabel>
                        </RowContent>
                      </Row>
                    </Rows>
                  </Section>
                </>
              ) : (
                <Section
                  typeaheadText="Copy"
                  isExpanded={expandedIds.has("copy-readonly")}
                  onExpandedChange={(isExpanded) =>
                    expandedSections.setExpanded("copy-readonly", isExpanded)
                  }
                  heading={
                    <SectionHeading>
                      <SectionIcon icon={IconCommonFileDouble} />
                      <SectionTitle>Copy</SectionTitle>
                      <SectionChevron />
                    </SectionHeading>
                  }
                >
                  <Rows>
                    <Row typeaheadText="Copy Note" onAction={closeThen(copyLesson)}>
                      <RowContent>
                        <RowIcon icon={IconCopy} />
                        <RowLabel>Copy Note</RowLabel>
                      </RowContent>
                    </Row>
                  </Rows>
                </Section>
              )}
            </Sections>
          </MenuRoot>
        )
      }}
    </DrilldownMenu>
  )
})
